// ===== BACKEND API CLIENT =====
// Handles communication with the backend API server
// Replaces direct Google Sheets API calls with backend endpoints

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL || '';
const EMPTY_DASHBOARD_METRICS = {
  totalSales: 0,
  totalPurchases: 0,
  totalExpenses: 0,
  totalCogs: 0,
  inventoryValue: 0,
  totalProfit: 0,
  pendingPayments: 0,
  activeCustomers: 0,
  inventoryItems: 0,
  currentMonthSales: 0,
  previousMonthSales: 0,
  salesGrowth: 0,
  currentMonthPurchases: 0,
  previousMonthPurchases: 0,
  purchasesGrowth: 0,
  currentMonthExpenses: 0,
  previousMonthExpenses: 0,
  currentMonthCogs: 0,
  previousMonthCogs: 0,
  currentMonthProfit: 0,
  previousMonthProfit: 0,
  profitGrowth: 0,
  currentMonthInventoryValue: 0,
  previousMonthInventoryValue: 0,
  inventoryGrowth: 0,
};

const isExpectedEmptyStateError = (error) => {
  const status = error?.response?.status;
  const method = (error?.config?.method || '').toLowerCase();
  const errorText = `${error?.response?.data?.error || ''} ${error?.response?.data?.message || ''}`.toLowerCase();

  if (method !== 'get') return false;
  if (![400, 404].includes(status)) return false;

  return (
    errorText.includes('company not found') ||
    errorText.includes('business owner authentication required') ||
    errorText.includes('authentication required') ||
    errorText.includes('no business found') ||
    errorText.includes('companyid is required') ||
    errorText.includes('business not found')
  );
};

const buildEmptyStateResponseData = (config = {}) => {
  const url = (config.url || '').split('?')[0];
  const params = config.params || {};
  const now = new Date().toISOString();
  const page = parseInt(params.page, 10) || 1;

  if (url === '/api/sales') {
    return {
      success: true,
      sales: [],
      totalSales: 0,
      currentPage: page,
      totalPages: 0,
      generatedAt: now
    };
  }

  if (url === '/api/purchases') {
    return {
      success: true,
      purchases: [],
      totalPurchases: 0,
      currentPage: page,
      totalPages: 0,
      retrievedAt: now
    };
  }

  if (url === '/api/expenses') {
    return {
      success: true,
      expenses: [],
      totalExpenses: 0,
      retrievedAt: now
    };
  }

  if (url === '/api/dealers') {
    return {
      success: true,
      dealers: [],
      totalDealers: 0,
      retrievedAt: now
    };
  }

  if (/^\/api\/dealers\/\d+\/payments$/.test(url)) {
    return {
      success: true,
      payments: [],
      totalPayments: 0,
      totalAmount: 0,
      retrievedAt: now
    };
  }

  if (url === '/api/analytics/inventory') {
    return {
      success: true,
      inventory: [],
      totalItems: 0,
      generatedAt: now
    };
  }

  if (url === '/api/analytics/dashboard') {
    return {
      success: true,
      metrics: { ...EMPTY_DASHBOARD_METRICS },
      generatedAt: now
    };
  }

  if (url === '/api/analytics/profit/summary') {
    return {
      success: true,
      profit: [],
      generatedAt: now
    };
  }

  if (url === '/api/analytics/overview') {
    return {
      success: true,
      overview: {
        crossSheetInsights: {
          overallHealth: 'NEUTRAL',
          keyMetrics: {
            totalRevenue: 0,
            totalCustomers: 0,
            totalItems: 0
          }
        }
      },
      generatedAt: now
    };
  }

  if (url === '/api/analytics/reports/overview') {
    return {
      success: true,
      summary: {
        monthly: {},
        yearly: {},
        inventory: {}
      },
      charts: {
        monthlyBreakdown: [],
        yearlyBreakdown: [],
        topDealers: [],
        topSellingProducts: [],
        dailySales: []
      },
      insights: {
        recommendations: [],
        alerts: []
      },
      filters: {
        year: params.year ? parseInt(params.year, 10) : null,
        month: params.month ? parseInt(params.month, 10) : null,
        monthName: ''
      },
      generatedAt: now
    };
  }

  if (url === '/api/invoices') {
    return {
      success: true,
      invoices: [],
      totalInvoices: 0,
      currentPage: page,
      totalPages: 0,
      generatedAt: now
    };
  }

  if (url === '/api/invoices/search') {
    return {
      success: true,
      invoices: [],
      pagination: {
        total: 0,
        limit: parseInt(params.limit, 10) || 20,
        offset: parseInt(params.offset, 10) || 0
      },
      generatedAt: now
    };
  }

  if (url === '/api/invoices/basic/customers') {
    return {
      success: true,
      customers: [],
      totalCustomers: 0,
      generatedAt: now
    };
  }

  if (url === '/api/invoices/basic/business') {
    return {
      success: true,
      business: {},
      generatedAt: now
    };
  }

  return null;
};

class BackendAPI {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include sheetId from localStorage for analytics and dealers endpoints
    this.client.interceptors.request.use((config) => {

      // Add Authorization header with JWT token if available
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }

      // Add timestamp to prevent caching issues with OAuth tokens
      config.params = { ...config.params, _t: Date.now() };

      // Handle Content-Type for different request types
      if (config.method === 'get') {
        delete config.headers['Content-Type'];
      } else if (config.data instanceof FormData) {
        // Let axios set Content-Type automatically for FormData (multipart/form-data)
        delete config.headers['Content-Type'];
      }

      // For auth endpoints, use userId
      if (config.url && config.url.includes('/auth/')) {
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            const parsedUserData = JSON.parse(userData);
            const userId = parsedUserData.id;
            if (userId) {
              if (config.method === 'post' || config.method === 'put') {
                if (config.data) {
                  let data = config.data;
                  if (typeof data === 'string') {
                    data = JSON.parse(data);
                  }
                  if (!data.userId && !data.user_id) {
                    data.userId = userId;
                    config.data = JSON.stringify(data);
                  }
                } else {
                  config.data = JSON.stringify({ userId });
                }
              }
            }
          } catch (error) {
            
          }
        }
      }
      // For analytics, dealers, and invoice endpoints, use userId and companyId if available
      else if (config.url && (config.url.includes('/analytics/') || config.url.includes('/dealers') || config.url.includes('/invoice/') || config.url.includes('/business/'))) {
        console.log('🔍 INTERCEPTOR: Processing analytics/invoice/business request:', config.url);

        // Skip processing for FormData requests
        if (config.data instanceof FormData) {
          console.log('🔍 INTERCEPTOR: Skipping FormData processing for analytics/invoice/business');
        } else {
          // Get userId and companyId from user data stored in localStorage
        const userData = localStorage.getItem('userData');
        console.log('🔍 INTERCEPTOR: userData from localStorage:', userData ? 'EXISTS' : 'NOT FOUND');
        if (userData) {
          try {
            const parsedUserData = JSON.parse(userData);
            const userId = parsedUserData.id;
            const companyId = parsedUserData.business?.id;
            console.log('🔍 INTERCEPTOR: parsed userId:', userId, 'companyId:', companyId);

            // Always add userId for GET requests
            if (config.method === 'get') {
              config.params = { ...config.params, userId };
              if (companyId) {
                config.params.companyId = companyId;
              }
            } else if (config.method === 'post' || config.method === 'put') {
              // For POST/PUT, add userId and companyId to the request body

              if (config.data) {
                let data = config.data;
                if (typeof data === 'string') {
                  data = JSON.parse(data);

                }

                if (!data.user_id && !data.userId) {
                  data.user_id = userId;
                }
                if (companyId && !data.company_id && !data.companyId) {
                  data.company_id = companyId;
                }
                config.data = JSON.stringify(data);

              } else {
                const requestData = { user_id: userId };
                if (companyId) {
                  requestData.company_id = companyId;
                }
                config.data = JSON.stringify(requestData);

              }
            }
          } catch (error) {

          }
        }
        }
      } else {
        // For other endpoints, use userId and companyId if available

        const userData = localStorage.getItem('userData');

        if (userData) {
          try {
            const parsedUserData = JSON.parse(userData);
            const userId = parsedUserData.id;
            const companyId = parsedUserData.business?.id;

            if (userId) {
              if (config.method === 'get') {
                config.params = { ...config.params, userId };
                if (companyId) {
                  config.params.companyId = companyId;
                }
              } else if (config.method === 'post' || config.method === 'put' || config.method === 'delete') {
                // For POST/PUT/DELETE, add userId and companyId to the request body if not already there

                if (config.data) {
                  let data = config.data;
                  if (typeof data === 'string') {
                    data = JSON.parse(data);

                  }

                  if (!data.user_id && !data.userId) {
                    data.user_id = userId;
                  }
                  if (companyId && !data.company_id && !data.companyId) {
                    data.company_id = companyId;
                  }

                  config.data = JSON.stringify(data);

                } else {
                  const requestData = { user_id: userId };
                  if (companyId) {
                    requestData.company_id = companyId;
                  }
                  config.data = JSON.stringify(requestData);

                }
              }
            }
          } catch (error) {

          }
        }
      }
      return config;
    });
// Response interceptor removed - no longer handling session expiration since using database auth
this.client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isExpectedEmptyStateError(error)) {
      const fallbackData = buildEmptyStateResponseData(error.config);
      if (fallbackData) {
        console.info('Using empty-state fallback response for:', error?.config?.url);
        return Promise.resolve({
          data: fallbackData,
          status: 200,
          statusText: 'OK',
          headers: error?.response?.headers || {},
          config: error.config,
          request: error.request
        });
      }
    }
    return Promise.reject(error);
  }
);
  }

  // ===== SALES API =====
  async getSales(page = 1, limit = 10) {
    try {
      const response = await this.client.get('/api/sales', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async addSale(saleData) {
    try {
      const response = await this.client.post('/api/sales', saleData);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async updateSale(saleId, saleData) {
    try {
      const response = await this.client.put(`/api/sales/${saleId}`, saleData);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async deleteSale(saleId) {
    try {
      const response = await this.client.delete(`/api/sales/${saleId}`);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async getSaleById(saleId) {
    try {
      const response = await this.client.get(`/api/sales/${saleId}`);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  // ===== PURCHASE API =====
  async getPurchases(page = 1, limit = 10) {
    try {
      const response = await this.client.get('/api/purchases', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async addPurchase(purchaseData) {
    try {
      const response = await this.client.post('/api/purchases', purchaseData);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async updatePurchase(purchaseId, purchaseData) {
    try {
      const response = await this.client.put(`/api/purchases/${purchaseId}`, purchaseData);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async deletePurchase(purchaseId) {
    try {
      const response = await this.client.delete(`/api/purchases/${purchaseId}`);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async getPurchaseById(purchaseId) {
    try {
      const response = await this.client.get(`/api/purchases/${purchaseId}`);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  // ===== EXPENSES API =====
  async getExpenses() {
    try {
      const response = await this.client.get('/api/expenses');
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async addExpense(expenseData) {
    try {
      const response = await this.client.post('/api/expenses', expenseData);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async updateExpense(expenseId, expenseData) {
    try {
      const response = await this.client.put(`/api/expenses/${expenseId}`, expenseData);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  async deleteExpense(expenseId) {
    try {
      const response = await this.client.delete(`/api/expenses/${expenseId}`);
      return response.data;
    } catch (error) {
      
      throw error;
    }
  }

  // ===== INVOICES API =====
  async getInvoices(page = 1, limit = 10) {
    try {
      const response = await this.client.get('/api/invoices', {
        params: { page, limit }
      });
      return response.data;

    } catch (error) {

      throw error;
    }
  }
  async getInvoiceById(invoiceId) {
    try {
      const response = await this.client.get(`/api/invoices/${invoiceId}`);
      return response.data;

    } catch (error) {

      throw error;
    }
  }
  async generateInvoice(invoiceData) {
    try {
      const response = await this.client.post('/api/invoices/generate', invoiceData);
      return response.data;

    } catch (error) {

      throw error;
    }
  }
  async updateInvoiceTemplate(templateData) {
    try {
      const response = await this.client.post('/api/invoice/template', { templateData });
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getInvoiceTemplate() {
    try {
      const response = await this.client.get('/api/invoice/template');
      return response.data;
    } catch (error) {

      throw error;
    }
  }
  async generateInvoicePDF(invoiceId, template = 'simple', printProfile = 'a4', options = {}) {
    try {
      const response = await this.client.post(`/api/invoices/${invoiceId}/generate-pdf`, {
        template,
        printProfile,
        forceRegenerate: options.forceRegenerate === true
      });
      return response.data;

    } catch (error) {

      throw error;
    }
  }

  async downloadInvoicePDF(invoiceId) {
    try {
      const response = await this.client.get(`/api/invoices/${invoiceId}/download-pdf`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async searchInvoices(searchParams) {
    try {
      const response = await this.client.get('/api/invoices/search', {
        params: searchParams
      });
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async generateInvoicePreview(previewData) {
    try {
      const response = await this.client.post('/api/invoices/preview', previewData);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async checkInvoiceStatus(saleIds) {
    try {
      const response = await this.client.post('/api/invoices/check-invoice-status', { saleIds });
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // ===== WHATSAPP API =====
  async sendWhatsAppText(payload) {
    try {
      const response = await this.client.post('/api/whatsapp/send-text', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async sendWhatsAppMedia(payload) {
    try {
      const response = await this.client.post('/api/whatsapp/send-media', payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async checkWhatsAppNumber(number, sender) {
    try {
      const response = await this.client.post('/api/whatsapp/check-number', { number, sender });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async sendInvoicePdfToCustomer(invoiceId, options = {}) {
    try {
      const response = await this.client.post('/api/whatsapp/send-invoice', {
        invoiceId,
        number: options.number,
        caption: options.caption,
        footer: options.footer,
        sender: options.sender,
        full: options.full ?? 1
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async sendCustomerSummaryPdf(customerId, options = {}) {
    try {
      const response = await this.client.post('/api/whatsapp/send-customer-summary', {
        customerId,
        number: options.number,
        caption: options.caption,
        footer: options.footer,
        htmlTemplate: options.htmlTemplate,
        sender: options.sender,
        full: options.full ?? 1
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ===== REMINDER AUTOMATION API =====
  async getReminderSettings() {
    try {
      const response = await this.client.get('/api/reminders/settings');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async saveReminderSettings(settings) {
    try {
      const response = await this.client.post('/api/reminders/settings', { settings });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async runReminderScanNow() {
    try {
      const response = await this.client.post('/api/reminders/run-now', { forceAllDue: true }, {
        timeout: 120000
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getCustomerPayments(customerId) {
    try {
      const response = await this.client.get(`/api/invoices/customers/${customerId}/payments`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getCustomerInvoices(customerId) {
    try {
      const response = await this.client.get(`/api/invoices/customers/${customerId}/invoices`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async recordCustomerPayment(customerId, paymentData) {
    try {
      const response = await this.client.post(`/api/invoices/customers/${customerId}/payments`, paymentData);
      return response.data;
    } catch (error) {

      throw error;
    }
  }
// ===== ANALYTICS API =====
async getDashboardMetrics() {
  try {
    const response = await this.client.get('/api/analytics/dashboard');
    return response.data;
  } catch (error) {

    throw error;
  }
}

async getAnalyticsOverview() {
  try {
    const response = await this.client.get('/api/analytics/overview');
    return response.data;
  } catch (error) {
    throw error;
  }
}

async getReportsOverview(year, month) {
  try {
    const response = await this.client.get('/api/analytics/reports/overview', {
      params: { year, month }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

async getProfitSummary() {
  try {
    const response = await this.client.get('/api/analytics/profit/summary');
    return response.data;
  } catch (error) {

    throw error;
  }
}

async getInventory() {
  try {
    const response = await this.client.get('/api/analytics/inventory');
    return response.data;
  } catch (error) {

    throw error;
  }
}

// Use lightweight invoice endpoints for basic data (avoids connection pool exhaustion)
async getCustomers() {
  try {
    const response = await this.client.get('/api/invoices/basic/customers');
    return response.data;

  } catch (error) {

    throw error;
  }
}

async updateCustomer(customerId, customerData) {
  try {
    const response = await this.client.put(`/api/invoices/customers/${customerId}`, customerData);
    return response.data;
  } catch (error) {
    throw error;
  }
}

  async getDealers() {
    try {
      const response = await this.client.get('/api/dealers');
      console.log('Dealers API response:', response.data); // Add debugging log
      return response.data.dealers || [];
    } catch (error) {
      console.error('Get dealers error:', error);
      throw error;
    }
  }

  async addDealer(dealerData) {
    try {
      const response = await this.client.post('/api/dealers', dealerData);
      return response.data;
    } catch (error) {
      console.error('Add dealer error:', error);
      throw error;
    }
  }

  async updateDealer(dealerId, dealerData) {
    try {
      const response = await this.client.put(`/api/dealers/${dealerId}`, dealerData);
      return response.data;
    } catch (error) {
      console.error('Update dealer error:', error);
      throw error;
    }
  }

  async deleteDealer(dealerId) {
    try {
      const response = await this.client.delete(`/api/dealers/${dealerId}`);
      return response.data;
    } catch (error) {
      console.error('Delete dealer error:', error);
      throw error;
    }
  }

  // ===== DEALER PAYMENTS API =====
  async getDealerPayments(dealerId) {
    try {
      const response = await this.client.get(`/api/dealers/${dealerId}/payments`);
      return response.data;
    } catch (error) {
      console.error('Get dealer payments error:', error);
      throw error;
    }
  }

  async addDealerPayment(dealerId, paymentData) {
    try {
      const response = await this.client.post(`/api/dealers/${dealerId}/payments`, paymentData);
      return response.data;
    } catch (error) {
      console.error('Add dealer payment error:', error);
      throw error;
    }
  }

  async updateDealerPayment(dealerId, paymentId, paymentData) {
    try {
      const response = await this.client.put(`/api/dealers/${dealerId}/payments/${paymentId}`, paymentData);
      return response.data;
    } catch (error) {
      console.error('Update dealer payment error:', error);
      throw error;
    }
  }

  async deleteDealerPayment(dealerId, paymentId) {
    try {
      const response = await this.client.delete(`/api/dealers/${dealerId}/payments/${paymentId}`);
      return response.data;
    } catch (error) {
      console.error('Delete dealer payment error:', error);
      throw error;
    }
  }

  // Use lightweight invoice endpoint for basic business data (avoids connection pool exhaustion)
  async getBusinessOverview() {
    try {
      const response = await this.client.get('/api/invoices/basic/business');
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async recalculateAll() {
    try {
      // No sheetId needed for database recalculations
      const response = await this.client.post('/api/analytics/recalculate');
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  // ===== DASHBOARD API =====
  async getDashboardData() {
    try {
      // Get business overview which includes all analytics data
      const overview = await this.getBusinessOverview();

      // Get recent sales and purchases separately for detailed view
      const [sales, purchases] = await Promise.all([
        this.getSales(1, 5), // Get last 5 sales
        this.getPurchases(1, 5) // Get last 5 purchases
      ]);

      return {
        overview: overview.overview,
        recentSales: sales.sales || [],
        recentPurchases: purchases.purchases || []
      };
    } catch (error) {
      
      throw error;
    }
  }

  // ===== USER SETTINGS API =====
  async getUserSettings(userId = null) {
    try {
      const targetUserId = userId || this.getUserId();
      if (!targetUserId) {
        throw new Error('User ID not found. Please log in again.');
      }

      const response = await this.client.get(`/api/user/settings/${targetUserId}`);
      return response.data;
    } catch (error) {
      console.error('Get user settings error:', error);
      throw error;
    }
  }

  async saveUserSettings(settings, userId = null) {
    try {
      const targetUserId = userId || this.getUserId();
      if (!targetUserId) {
        throw new Error('User ID not found. Please log in again.');
      }

      const response = await this.client.post('/api/user/settings', {
        userId: targetUserId,
        settings
      });
      return response.data;
    } catch (error) {
      console.error('Save user settings error:', error);
      throw error;
    }
  }

  // ===== STAFF ACCESS REQUESTS =====
  async submitStaffJoinRequest(businessId, businessName) {
    try {
      const response = await this.client.post('/api/business/staff/request', {
        businessId,
        businessName
      });
      return response.data;
    } catch (error) {
      console.error('Submit staff join request error:', error);
      throw error;
    }
  }

  async getStaffJoinRequestStatus() {
    try {
      const response = await this.client.get('/api/business/staff/request-status');
      return response.data;
    } catch (error) {
      console.error('Get staff join request status error:', error);
      throw error;
    }
  }

  async getPendingStaffRequests() {
    try {
      const response = await this.client.get('/api/business/staff/pending');
      return response.data;
    } catch (error) {
      console.error('Get pending staff requests error:', error);
      throw error;
    }
  }

  async getStaffMembers() {
    try {
      const response = await this.client.get('/api/business/staff');
      return response.data;
    } catch (error) {
      console.error('Get staff members error:', error);
      throw error;
    }
  }

  async approveStaffRequest(staffId, role = 'staff') {
    try {
      const response = await this.client.put(`/api/business/staff/${staffId}/approve`, { role });
      return response.data;
    } catch (error) {
      console.error('Approve staff request error:', error);
      throw error;
    }
  }

  async rejectStaffRequest(staffId) {
    try {
      const response = await this.client.put(`/api/business/staff/${staffId}/reject`);
      return response.data;
    } catch (error) {
      console.error('Reject staff request error:', error);
      throw error;
    }
  }

  async updateStaffRole(staffId, role = 'staff') {
    try {
      const response = await this.client.put(`/api/business/staff/${staffId}/role`, { role });
      return response.data;
    } catch (error) {
      console.error('Update staff role error:', error);
      throw error;
    }
  }

  async removeStaffAccess(staffId) {
    try {
      const response = await this.client.delete(`/api/business/staff/${staffId}`);
      return response.data;
    } catch (error) {
      console.error('Remove staff access error:', error);
      throw error;
    }
  }

  // ===== UTILITY METHODS =====
  setUserId(userId) {
    // User ID is now stored in userData, this method is kept for compatibility

  }

  getUserId() {
    // Extract user ID from stored userData
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const parsedUserData = JSON.parse(userData);
        return parsedUserData.id;
      } catch (error) {

        return null;
      }
    }
    return null;
  }

  clearUserId() {
    // User data is cleared by Auth component, this method is kept for compatibility

  }
}

export default new BackendAPI();
